/**
 * Map Component
 *
 * D3-powered interactive map of the Netherlands displaying organization markers.
 * Features:
 * - TopoJSON rendering of municipality/province boundaries
 * - Force-directed layout preventing marker overlap
 * - Zoom/pan with collision detection
 * - Organization markers with logos/colored dots
 * - Rich tooltips on hover
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { COLORS, MAP_CONFIG } from '../../lib/constants';
import type { Organization } from '../../types';

// TopoJSON types (inline to avoid extra dependency)
interface TopoJSONTopology {
  type: 'Topology';
  objects: Record<string, TopoJSONGeometryCollection>;
  arcs: number[][][];
  transform?: { scale: [number, number]; translate: [number, number] };
}

interface TopoJSONGeometryCollection {
  type: 'GeometryCollection';
  geometries: TopoJSONGeometry[];
}

interface TopoJSONGeometry {
  type: string;
  id?: string | number;
  arcs?: number[][] | number[][][];
  properties?: Record<string, unknown>;
}

// Simple TopoJSON feature conversion (subset of topojson-client)
function topoFeature(
  topology: TopoJSONTopology,
  objectName: string
): GeoJSON.FeatureCollection {
  const obj = topology.objects[objectName];
  if (!obj || obj.type !== 'GeometryCollection') {
    return { type: 'FeatureCollection', features: [] };
  }

  const features: GeoJSON.Feature[] = obj.geometries.map((geom) => {
    const coordinates = geom.arcs ? arcToCoordinates(topology, geom) : [];

    let geometry: GeoJSON.Geometry;
    if (geom.type === 'Polygon') {
      geometry = {
        type: 'Polygon',
        coordinates: coordinates as GeoJSON.Position[][],
      };
    } else if (geom.type === 'MultiPolygon') {
      geometry = {
        type: 'MultiPolygon',
        coordinates: coordinates as GeoJSON.Position[][][],
      };
    } else {
      // Fallback - treat as Polygon
      geometry = {
        type: 'Polygon',
        coordinates: coordinates as GeoJSON.Position[][],
      };
    }

    return {
      type: 'Feature' as const,
      id: geom.id,
      properties: geom.properties || {},
      geometry,
    };
  });

  return { type: 'FeatureCollection', features };
}

function arcToCoordinates(
  topology: TopoJSONTopology,
  geom: TopoJSONGeometry
): number[][][] | number[][][][] {
  const { arcs, transform } = topology;

  function decodeArc(arcIndex: number): number[][] {
    const arc = arcs[arcIndex < 0 ? ~arcIndex : arcIndex];
    const coords: number[][] = [];
    let x = 0, y = 0;

    for (const [dx, dy] of arc) {
      x += dx;
      y += dy;
      coords.push([x, y]);
    }

    // Apply transform if present
    let result = coords;
    if (transform) {
      const { scale, translate } = transform;
      result = coords.map(([cx, cy]) => [
        cx * scale[0] + translate[0],
        cy * scale[1] + translate[1],
      ]);
    }

    return arcIndex < 0 ? result.reverse() : result;
  }

  function decodeRing(indices: number[]): number[][] {
    const ring: number[][] = [];
    for (const idx of indices) {
      const decoded = decodeArc(idx);
      // Skip first point of subsequent arcs (it's the same as last of previous)
      ring.push(...(ring.length === 0 ? decoded : decoded.slice(1)));
    }
    return ring;
  }

  if (geom.type === 'Polygon') {
    return (geom.arcs as number[][]).map(decodeRing);
  } else if (geom.type === 'MultiPolygon') {
    return (geom.arcs as number[][][]).map((polygon) =>
      polygon.map(decodeRing)
    );
  }

  return [];
}

interface MapProps {
  organizations?: Organization[];
  onOrganizationClick?: (org: Organization) => void;
}

// Circle node for force simulation
interface CircleNode extends d3.SimulationNodeDatum {
  id: string;
  org: Organization;
  // Base position (from projection)
  baseX: number;
  baseY: number;
  // Current position (modified by collision)
  x: number;
  y: number;
}

// Tooltip state
interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  org: Organization | null;
}

// URLs for TopoJSON data
const GEMEENTE_URL = 'https://cartomap.github.io/nl/wgs84/gemeente_2025.topojson';
const PROVINCIE_URL = 'https://cartomap.github.io/nl/wgs84/provincie_2024.topojson';
const EUROPE_URL = 'https://unpkg.com/world-atlas@2/countries-50m.json';

// Map colors
const MAP_COLORS = {
  northSea: '#d4e9f7',
  neighboringCountry: '#e0e0e0',
  neighboringCountryStroke: '#cccccc',
};

// ISO 3166-1 numeric codes for neighboring countries
const NEIGHBORING_COUNTRY_CODES = [56, 276, 442, 826, 250]; // BE, DE, LU, UK, FR

function isNeighboringCountry(id: unknown): boolean {
  if (id === undefined || id === null) return false;
  const numericId = typeof id === 'number' ? id : parseInt(String(id), 10);
  return NEIGHBORING_COUNTRY_CODES.includes(numericId);
}

const styles = {
  container: {
    flex: 1,
    position: 'relative' as const,
    backgroundColor: MAP_COLORS.northSea,
    minHeight: '400px',
    cursor: 'grab',
    overflow: 'hidden',
  },
  svg: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
  },
  tooltip: {
    position: 'fixed' as const,
    backgroundColor: '#ffffff',
    border: `1px solid ${COLORS.graphite}30`,
    borderRadius: 0,
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    pointerEvents: 'none' as const,
    zIndex: 1000,
    fontSize: '15px',
    maxWidth: '360px',
  },
  tooltipCity: {
    color: `${COLORS.graphite}99`,
    marginTop: 3,
  },
  tooltipServices: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '5px',
    marginTop: '8px',
  },
  serviceTag: {
    padding: '2px 8px',
    backgroundColor: `${COLORS.graphite}10`,
    borderRadius: 0,
    fontSize: '13px',
    color: `${COLORS.graphite}99`,
  },
};

export function Map({ organizations = [], onOrganizationClick }: MapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [gemeenteData, setGemeenteData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [provincieData, setProvincieData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [europeData, setEuropeData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    org: null,
  });

  // Fetch TopoJSON data
  useEffect(() => {
    fetch(GEMEENTE_URL)
      .then((res) => res.json())
      .then((topology: TopoJSONTopology) => {
        const objectKey = Object.keys(topology.objects)[0];
        setGemeenteData(topoFeature(topology, objectKey));
      })
      .catch((err) => console.error('Failed to load gemeente data:', err));

    fetch(PROVINCIE_URL)
      .then((res) => res.json())
      .then((topology: TopoJSONTopology) => {
        const objectKey = Object.keys(topology.objects)[0];
        setProvincieData(topoFeature(topology, objectKey));
      })
      .catch((err) => console.error('Failed to load provincie data:', err));

    // Fetch Europe/world data for context
    fetch(EUROPE_URL)
      .then((res) => res.json())
      .then((topology: TopoJSONTopology) => {
        const allCountries = topoFeature(topology, 'countries');
        // Filter to only neighboring countries
        const neighboringCountries: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: allCountries.features.filter((f) => isNeighboringCountry(f.id)),
        };
        setEuropeData(neighboringCountries);
      })
      .catch((err) => console.error('Failed to load Europe data:', err));
  }, []);

  // Track container size
  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Filter to orgs with coordinates
  const mappedOrgs = useMemo(
    () => organizations.filter((org) => org.lat && org.lng),
    [organizations]
  );

  // Create projection fitted to container
  const projection = useMemo(() => {
    if (!gemeenteData || dimensions.width === 0) return null;

    const padding = 40;
    return d3.geoMercator().fitExtent(
      [
        [padding, padding],
        [dimensions.width - padding, dimensions.height - padding],
      ],
      gemeenteData
    );
  }, [gemeenteData, dimensions]);

  // Main D3 effect - renders map and markers with zoom/pan
  useEffect(() => {
    if (!svgRef.current || !gemeenteData || !projection || dimensions.width === 0) return;

    const svg = d3.select(svgRef.current);
    const path = d3.geoPath(projection);

    // Clear previous content
    svg.selectAll('*').remove();

    // Defs for clip paths and filters
    const defs = svg.append('defs');

    // Circle clip for logos
    defs
      .append('clipPath')
      .attr('id', 'circle-clip')
      .attr('clipPathUnits', 'objectBoundingBox')
      .append('circle')
      .attr('cx', 0.5)
      .attr('cy', 0.5)
      .attr('r', 0.5);

    // Subtle drop shadow for markers
    const markerShadow = defs.append('filter').attr('id', 'marker-shadow');
    markerShadow
      .append('feDropShadow')
      .attr('dx', 0)
      .attr('dy', 1)
      .attr('stdDeviation', 1.5)
      .attr('flood-color', COLORS.graphite)
      .attr('flood-opacity', 0.25);

    // Main group for all zoomable content
    const mainGroup = svg.append('g').attr('class', 'main-group');

    // Sea background (North Sea blue)
    mainGroup
      .append('rect')
      .attr('class', 'sea-background')
      .attr('x', -dimensions.width)
      .attr('y', -dimensions.height)
      .attr('width', dimensions.width * 3)
      .attr('height', dimensions.height * 3)
      .attr('fill', MAP_COLORS.northSea);

    // European neighboring countries (background context layer)
    if (europeData) {
      const europeGroup = mainGroup.append('g').attr('class', 'europe-context');

      europeGroup
        .selectAll('path')
        .data(europeData.features)
        .join('path')
        .attr('d', path)
        .attr('fill', MAP_COLORS.neighboringCountry)
        .attr('stroke', MAP_COLORS.neighboringCountryStroke)
        .attr('stroke-width', 0.5);
    }

    // Municipalities group (base layer with fill) - Netherlands on top
    const gemeenteGroup = mainGroup.append('g').attr('class', 'gemeenten');

    gemeenteGroup
      .selectAll('path')
      .data(gemeenteData.features)
      .join('path')
      .attr('d', path)
      .attr('fill', COLORS.parchment)
      .attr('stroke', COLORS.graphite)
      .attr('stroke-width', 0.3)
      .attr('stroke-opacity', 0.2);

    // Provinces group (overlay with stronger borders)
    if (provincieData) {
      const provincieGroup = mainGroup.append('g').attr('class', 'provincies');

      provincieGroup
        .selectAll('path')
        .data(provincieData.features)
        .join('path')
        .attr('d', path)
        .attr('fill', 'none')
        .attr('stroke', COLORS.graphite)
        .attr('stroke-width', 0.75)
        .attr('stroke-opacity', 0.25);
    }

    // Organization markers group
    const markersGroup = mainGroup.append('g').attr('class', 'markers');

    // Track current zoom transform for marker scaling
    let currentTransform = d3.zoomIdentity;
    let lastScale = 1;

    const markerSize = MAP_CONFIG.markerRadius.default;
    const hoverSize = markerSize + 2;
    const baseCollisionRadius = MAP_CONFIG.forceSimulation.collisionRadius;

    // Helper to calculate marker size based on organization properties
    function getMarkerSize(org: Organization): number {
      const isDembrane = org.is_dembrane_user;
      if (org.logo_url) {
        return isDembrane ? MAP_CONFIG.markerRadius.dembranePartner : markerSize;
      }
      return isDembrane ? MAP_CONFIG.markerRadius.dembranePartner : MAP_CONFIG.markerRadius.noLogo;
    }

    // Helper to apply size to marker elements
    function applyMarkerSize(group: d3.Selection<SVGGElement, CircleNode, null, undefined>, size: number, scale: number, hasLogo: boolean): void {
      const adjustedSize = size / scale;
      if (hasLogo) {
        group.select('.logo-bg').attr('r', adjustedSize + 1 / scale);
        group.select('.logo-img')
          .attr('width', adjustedSize * 2)
          .attr('height', adjustedSize * 2)
          .attr('x', -adjustedSize)
          .attr('y', -adjustedSize);
      } else {
        group.select('.acronym-bg').attr('r', adjustedSize);
        group.select('.acronym-text').attr('font-size', adjustedSize * 0.8);
      }
    }

    // Initialize nodes for force simulation
    const circleNodes: CircleNode[] = [];

    mappedOrgs.forEach((org) => {
      const projected = projection([org.lng!, org.lat!]);
      if (!projected) return;

      const [x, y] = projected;

      circleNodes.push({
        id: org.domain || org.name,
        org,
        baseX: x,
        baseY: y,
        x,
        y,
      });
    });

    // Create force simulation with collision detection
    const simulation = d3
      .forceSimulation<CircleNode>(circleNodes)
      .force(
        'x',
        d3.forceX<CircleNode>((d) => d.baseX).strength(0.8)
      )
      .force(
        'y',
        d3.forceY<CircleNode>((d) => d.baseY).strength(0.8)
      )
      .force(
        'collision',
        d3
          .forceCollide<CircleNode>()
          .radius(baseCollisionRadius)
          .strength(MAP_CONFIG.forceSimulation.collisionStrength)
          .iterations(2)
      )
      .alphaDecay(0.05)
      .velocityDecay(0.4);

    // Function to update collision radius and reheat simulation on zoom
    const handleZoomChange = (scale: number) => {
      // Collision radius shrinks as we zoom in (markers take less relative space)
      const adjustedCollisionRadius = baseCollisionRadius / scale;

      simulation
        .force(
          'collision',
          d3
            .forceCollide<CircleNode>()
            .radius(adjustedCollisionRadius)
            .strength(MAP_CONFIG.forceSimulation.collisionStrength)
            .iterations(2)
        )
        .alpha(0.3)
        .restart();
    };

    // Create group elements for each organization
    const groups = markersGroup
      .selectAll<SVGGElement, CircleNode>('g.org-marker')
      .data(circleNodes, (d) => d.id)
      .join('g')
      .attr('class', 'org-marker')
      .attr('cursor', 'pointer')
      .style('pointer-events', 'all');

    // Function to update marker sizes based on zoom scale
    function updateMarkerSizes(scale: number): void {
      groups.each(function (d) {
        const group = d3.select<SVGGElement, CircleNode>(this);
        const baseSize = getMarkerSize(d.org);
        const strokeWidth = (d.org.is_dembrane_user ? 2 : 1) / scale;

        applyMarkerSize(group, baseSize, scale, !!d.org.logo_url);

        // Also update stroke width
        if (d.org.logo_url) {
          group.select('.logo-bg').attr('stroke-width', strokeWidth);
        } else {
          group.select('.acronym-bg').attr('stroke-width', strokeWidth);
        }
      });
    }

    // Get acronym from organization name (first 2 letters)
    const getAcronym = (name: string) => {
      const words = name.trim().split(/\s+/);
      if (words.length >= 2) {
        // Use first letter of first two words
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      // Use first two letters of single word
      return name.slice(0, 2).toUpperCase();
    };

    // Create markers (logos or circles)
    groups.each(function (d) {
      const group = d3.select(this);
      group.attr('filter', 'url(#marker-shadow)');

      const isDembranePartner = d.org.is_dembrane_user;
      const size = getMarkerSize(d.org);

      // Dembrane partners get green stroke, others no stroke (unless no logo)
      const dembraneStroke = isDembranePartner ? COLORS.springGreen : 'none';
      const dembraneStrokeWidth = isDembranePartner ? 2 : 0;

      if (d.org.logo_url) {
        group
          .append('circle')
          .attr('class', 'logo-bg')
          .attr('r', size + 1)
          .attr('fill', '#ffffff')
          .attr('stroke', dembraneStroke)
          .attr('stroke-width', dembraneStrokeWidth);

        group
          .append('image')
          .attr('class', 'logo-img')
          .attr('href', d.org.logo_url)
          .attr('width', size * 2)
          .attr('height', size * 2)
          .attr('x', -size)
          .attr('y', -size)
          .attr('clip-path', 'url(#circle-clip)')
          .attr('preserveAspectRatio', 'xMidYMid slice');
      } else {
        // No logo: white fill with blue border (or green for Dembrane)
        const noLogoStroke = isDembranePartner ? COLORS.springGreen : COLORS.royalBlue;
        group
          .append('circle')
          .attr('class', 'acronym-bg')
          .attr('r', size)
          .attr('fill', '#ffffff')
          .attr('stroke', noLogoStroke)
          .attr('stroke-width', isDembranePartner ? 2 : 1);

        group
          .append('text')
          .attr('class', 'acronym-text')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', size * 0.8)
          .attr('font-weight', '300')
          .attr('fill', COLORS.graphite)
          .attr('style', 'font-family: "DM Sans", -apple-system, sans-serif')
          .text(getAcronym(d.org.name));
      }
    });

    // Hover and click handlers
    groups
      .on('mouseenter', function (event, d) {
        const group = d3.select<SVGGElement, CircleNode>(this);
        applyMarkerSize(group, hoverSize, currentTransform.k, !!d.org.logo_url);

        setTooltip({
          visible: true,
          x: event.clientX,
          y: event.clientY,
          org: d.org,
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => ({
          ...prev,
          x: event.clientX,
          y: event.clientY,
        }));
      })
      .on('mouseleave', function (_, d) {
        const group = d3.select<SVGGElement, CircleNode>(this);
        applyMarkerSize(group, getMarkerSize(d.org), currentTransform.k, !!d.org.logo_url);
        setTooltip((prev) => ({ ...prev, visible: false }));
      })
      .on('click', function (_, d) {
        if (onOrganizationClick) {
          onOrganizationClick(d.org);
        } else if (d.org.website) {
          window.open(d.org.website, '_blank', 'noopener,noreferrer');
        }
      });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      groups.attr('transform', (d) => `translate(${d.x}, ${d.y})`);
    });

    // Calculate pan constraints based on the Netherlands bounds
    // Allow some padding beyond the map but keep Netherlands generally in view
    const bounds = path.bounds(gemeenteData);
    const [[x0, y0], [x1, y1]] = bounds;
    const mapWidth = x1 - x0;
    const mapHeight = y1 - y0;

    // Add generous padding (50% of map size) to allow some exploration
    // but prevent completely losing the Netherlands from view
    const panPadding = Math.max(mapWidth, mapHeight) * 0.5;
    const translateExtent: [[number, number], [number, number]] = [
      [x0 - panPadding, y0 - panPadding],
      [x1 + panPadding, y1 + panPadding]
    ];

    // Setup zoom behavior with pan constraints
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 8])
      .translateExtent(translateExtent)
      .on('zoom', (event) => {
        currentTransform = event.transform;
        mainGroup.attr('transform', event.transform.toString());

        // Counter-scale markers to maintain consistent visual size
        updateMarkerSizes(event.transform.k);

        // Also adjust stroke widths on map paths
        gemeenteGroup.selectAll('path')
          .attr('stroke-width', 0.3 / event.transform.k);

        if (provincieData) {
          mainGroup.select('.provincies').selectAll('path')
            .attr('stroke-width', 0.75 / event.transform.k);
        }

        // Scale Europe context layer strokes
        mainGroup.select('.europe-context').selectAll('path')
          .attr('stroke-width', 0.5 / event.transform.k);

        // Detect zoom (scale change) vs pan (translate only)
        if (event.transform.k !== lastScale) {
          lastScale = event.transform.k;
          handleZoomChange(event.transform.k);
        }
      });

    svg.call(zoom);

    // Double-click to reset zoom
    svg.on('dblclick.zoom', () => {
      svg.transition().duration(300).call(zoom.transform, d3.zoomIdentity);
    });

    return () => {
      simulation.stop();
    };
  }, [gemeenteData, provincieData, europeData, projection, mappedOrgs, dimensions, onOrganizationClick]);

  return (
    <div
      ref={containerRef}
      style={styles.container}
    >
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        style={styles.svg}
      />

      {/* Tooltip */}
      {tooltip.visible && tooltip.org && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x + 12,
            top: tooltip.y - 12,
            transform: 'translateY(-100%)',
          }}
        >
          <strong>{tooltip.org.name}</strong>
          {tooltip.org.city && (
            <div style={styles.tooltipCity}>
              {tooltip.org.city}
            </div>
          )}

          {/* Services/tags */}
          {tooltip.org.services && tooltip.org.services.length > 0 && (
            <div style={styles.tooltipServices}>
              {tooltip.org.services.slice(0, 4).map((service) => (
                <span
                  key={service}
                  style={styles.serviceTag}
                >
                  {service.replace(/-/g, ' ')}
                </span>
              ))}
              {tooltip.org.services.length > 4 && (
                <span style={{ fontSize: '11px', color: `${COLORS.graphite}60` }}>
                  +{tooltip.org.services.length - 4}
                </span>
              )}
            </div>
          )}

          {/* Dembrane partner badge */}
          {tooltip.org.is_dembrane_user && (
            <div style={{
              marginTop: '6px',
              fontSize: '11px',
              color: COLORS.royalBlue,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <img
                src="/logos/dembrane-logomark.svg"
                alt="Dembrane"
                style={{
                  width: '16px',
                  height: '16px',
                  flexShrink: 0
                }}
              />
              Verified Dembrane partner
            </div>
          )}

          {/* Social image preview */}
          {tooltip.org.og_image_url && (
            <div
              style={{
                marginTop: '8px',
                borderRadius: 0,
                overflow: 'hidden',
                backgroundColor: `${COLORS.graphite}08`,
              }}
            >
              <img
                src={tooltip.org.og_image_url}
                alt=""
                style={{
                  width: '100%',
                  height: '100px',
                  objectFit: 'cover',
                  display: 'block',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}

          {/* SEO description */}
          {(tooltip.org.og_description || tooltip.org.description) && (
            <div
              style={{
                marginTop: '8px',
                color: `${COLORS.graphite}BB`,
                fontSize: '11px',
                lineHeight: 1.4,
                fontStyle: tooltip.org.og_description ? 'italic' : 'normal',
              }}
            >
              {(tooltip.org.og_description || tooltip.org.description)!.slice(0, 150)}
              {(tooltip.org.og_description || tooltip.org.description)!.length > 150 &&
                '...'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
